import forOwn from 'lodash/forOwn';
import { Pipe, PipeTransform } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { SingularPluralsMap as i18nSingularPluralsMap, II18nSingularPlural } from '../lib/i18n-backend-strings';

interface IDuration {
  year?: number;
  month?: number;
  week?: number;
  wk?: number;
  day?: number;
  hr?: number;
  min?: number;
  sec?: number;
  yr?: number;
  mo?: number;
  w?: number;
  d?: number;
  h?: number;
  m?: number;
  s?: number;
}

@Pipe({ name: 'duration' })
export class DurationPipe implements PipeTransform {
  private durations: IDuration;
  private i18nSingularPluralsMap: II18nSingularPlural = i18nSingularPluralsMap;

  constructor(private decimalPipe: DecimalPipe) { }

  public transform(
    timeInSec: number,
    showSeconds: boolean = true,
    showDays: boolean = false,
    showMonth: boolean = false,
    showYear: boolean = false,
    roundOff: boolean = true,
    useShortHandUnit: boolean = false,
    postFixString: string = '',
    hideMinutesForThreeDigitHourValue: boolean = false,
    showWeek: boolean = false,
    showMins: boolean = true,
    lessThanUnit: string = 'day',
    hideMinutesInHourValue: boolean = false,
    showLeadingZero: boolean = false,
    useCompactFormat: boolean = false,
    showZeroValues: boolean = false,
  ) {
    if (timeInSec == null) return;

    if (lessThanUnit === 'day') {
      const durations: IDuration = this.getElapsedDuration(timeInSec, showYear, showMonth, showWeek, showDays, true, showSeconds,
        useShortHandUnit, hideMinutesInHourValue, hideMinutesForThreeDigitHourValue, useCompactFormat);

      const largestUnit: IDuration = this.getLargestAvailableUnit(durations);
      if (largestUnit && largestUnit[useShortHandUnit ? 'd' : 'day'] < 1) {
        return $localize`Less than a day`;
      }
    }

    this.durations = this.getElapsedDuration(timeInSec, showYear, showMonth, showWeek, showDays, showMins, showSeconds,
      useShortHandUnit, hideMinutesInHourValue, hideMinutesForThreeDigitHourValue, useCompactFormat);

    const haveDays = this.durations[useShortHandUnit ? 'd' : 'day'] > 0;
    const haveWeeks = this.durations[useShortHandUnit ? 'w' : 'week'] > 0;

    if (roundOff && (haveDays || haveWeeks)) this.durations = this.roundToSingleLargestUnit();

    const output = this.formatOutput(showSeconds, showMins, useShortHandUnit, showLeadingZero, useCompactFormat, showZeroValues);

    if (postFixString === 'hace') {
      return `${postFixString} ${output}`;
    }

    if (postFixString.length) {
      return `${output} ${postFixString}`;
    }

    return output;
  }

  private getPluralPostfix(postfix, useCompactFormat) {
    if ((postfix === 'hr' || postfix === 'yr') && useCompactFormat) {
      return this.i18nSingularPluralsMap[postfix].singular;
    }
    return this.i18nSingularPluralsMap[postfix].plural;
  }

  private formatOutput(showSeconds: boolean, showMins: boolean, useShortHandUnit: boolean,
    showLeadingZero: boolean = false, useCompactFormat = false, showZeroValues: boolean = false): string {
    let output = '';
    forOwn(this.durations, (value: number, postfix: string): void => {
      const decimalFormat: string = showLeadingZero ? '2.0-1' : '1.0-1';
      if (value > 0 || (showZeroValues && value === 0)) {
        const formattedValue = this.decimalPipe.transform(value, decimalFormat);
        if (useShortHandUnit) {
          output += `${formattedValue}${postfix} `;
        } else {
          postfix =
            value === 1 ? this.i18nSingularPluralsMap[postfix].singular : this.getPluralPostfix(postfix, useCompactFormat);
          output += `${formattedValue} ${postfix} `;
        }
      }
    });

    if (!output) {
      const leadingZero: string = showLeadingZero ? '00' : '0';
      const minimumTimeUnit = (showMins && showSeconds) ? $localize`sec` : showMins ? $localize`min` : $localize`hr`;
      output = `${leadingZero}${useShortHandUnit ? minimumTimeUnit.charAt(0) : ` ${minimumTimeUnit}`}`;
    }

    return output.trim();
  }

  private roundToSingleLargestUnit(): IDuration {
    for (const [key, value] of Object.entries(this.durations)) {
      if (value > 0) return { [key]: value };
    }
  }

  private getLargestAvailableUnit(durations: IDuration): IDuration {
    for (const [key, value] of Object.entries(durations)) {
      if (value >= 0) return { [key]: value };
    }
  }

  private getElapsedDuration(
    timeInSec: number,
    showYear: boolean,
    showMonth: boolean,
    showWeek: boolean,
    showDays: boolean,
    showMins: boolean,
    showSeconds: boolean,
    useShortHandUnit: boolean,
    hideMinutesInHourValue: boolean,
    hideMinutesForThreeDigitHourValue: boolean,
    useCompactFormat: boolean = false,
  ): IDuration {
    let sec = Math.floor(timeInSec % 60);
    const timeInMins = showSeconds ? Math.floor(timeInSec / 60) : Math.round(timeInSec / 60);
    let min = timeInMins % 60;
    const timeInHrs = showMins ? Math.floor(timeInMins / 60) : Math.round(timeInMins / 60);
    const hr = showDays ? timeInHrs % 24 : timeInHrs;
    const timeInDays = Math.floor(timeInHrs / 24);
    let day = showWeek ? timeInDays % 7 : timeInDays;
    let week = Math.floor(timeInDays / 7);
    let month = Math.floor(timeInDays / 30);
    let year = Math.floor(month / 12);

    if (!showYear) year = -1;
    if (!showMonth) month = -1;
    if (!showDays) day = -1;
    if (!showWeek) week = -1;
    if (!showMins) min = -1;
    if (!showSeconds) sec = -1;
    if (hideMinutesForThreeDigitHourValue && hr >= 100 || (hideMinutesInHourValue && hr > 0)) min = -1;

    if (useCompactFormat) {
      return { yr: year, mo: month, wk: week, day, hr, min, sec };
    }

    return useShortHandUnit ? { yr: year, mo: month, w: week, d: day, h: hr, m: min, s: sec } :
      { year, month, week, day, hr, min, sec };
  }
}
